import { useMetaAccount } from "@/contexts/MetaAccountContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Plus, Building2 } from "lucide-react";
import { useLocation } from "wouter";
import { useState } from "react";

function getStatusDot(status?: string | null, syncStatus?: string | null) {
  if (status === "expired" || status === "revoked" || status === "error") return "bg-red-500";
  if (syncStatus === "error") return "bg-red-500";
  if (syncStatus === "syncing") return "bg-green-500";
  return "bg-green-500";
}

export function AccountSwitcher() {
  const { selectedAccountId, setSelectedAccountId, allAccounts, isLoading } = useMetaAccount();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const selected = selectedAccountId
    ? allAccounts.find(a => a.id === selectedAccountId)
    : null;

  const displayName = selected
    ? (selected.nickname || selected.accountName)
    : allAccounts.length === 0
      ? "No accounts"
      : "All Accounts";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-3 py-2 h-auto text-left text-sm text-white hover:bg-gray-800"
          data-testid="account-switcher"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
            <span className="truncate">{displayName}</span>
          </div>
          <ChevronDown className="h-3 w-3 shrink-0 text-gray-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start" side="right">
        <div className="flex flex-col">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2 text-sm"
            data-testid="account-option-all"
            onClick={() => { setSelectedAccountId(null); setOpen(false); }}
          >
            {selectedAccountId === null && <Check className="h-3 w-3 text-indigo-500" />}
            {selectedAccountId !== null && <span className="w-3" />}
            All Accounts
          </Button>

          {allAccounts.length > 0 && <div className="border-t my-1" />}

          {allAccounts.map(account => (
            <Button
              key={account.id}
              variant="ghost"
              size="sm"
              className="justify-start gap-2 text-sm"
              data-testid={`account-option-${account.id}`}
              onClick={() => { setSelectedAccountId(account.id); setOpen(false); }}
            >
              {selectedAccountId === account.id && <Check className="h-3 w-3 text-indigo-500" />}
              {selectedAccountId !== account.id && <span className="w-3" />}
              <span className={`w-2 h-2 rounded-full ${getStatusDot(account.connection?.status, account.syncStatus)}`} />
              <span className="truncate">{account.nickname || account.accountName}</span>
            </Button>
          ))}

          <div className="border-t my-1" />
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2 text-sm text-indigo-500"
            data-testid="account-connect-new"
            onClick={() => { navigate("/canvas?settings=connections"); setOpen(false); }}
          >
            <Plus className="h-3 w-3" />
            Connect New
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
